import { favoritesAssignWidgetReducer } from '@cian/favorites-assign-widget';
import {
  applyMiddleware,
  CombinedState,
  combineReducers,
  compose,
  createStore as _createStore,
  StoreEnhancer,
} from 'redux';
import thunk, { ThunkDispatch } from 'redux-thunk';
import {
  analyticsReducer,
  breadcrumbsReducer,
  errorReducer,
  filterSelectorsReducer,
  hasNexPageReducer,
  isCompilationReducer,
  jsonQueryReducer,
  loadingStatusReducer,
  nextJsonQueryReducer,
  noopReducer,
  pathReducer,
  promoDetailsReducer,
  promoOffersReducer,
  promosReducer,
  seoDataReducer,
  sortVariantsReducer,
  tagsReducer,
  totalCountReducer,
  userReducer,
} from '../../reducers';
import { listingsUrlsReducer } from '../../reducers/listingsUrls';
import { stickyButtonReducer } from '../../reducers/stickyButton';
import { suggestionsReducer } from '../../reducers/suggestions';
import { IApplicationContext } from '../../types/applicationContext';
import { IExperiment } from '../../types/experiments';
import { TPage } from '../../types/page';
import { IApplicationInitialState, IApplicationState, TReduxActions, TReduxStore } from '../../types/redux';

let storeReminder: TReduxStore;

interface IThunkExt {
  dispatch: ThunkDispatch<IApplicationState, IApplicationContext, TReduxActions>;
}

/**
 * Хелпер для создания Redux стора
 * @param initialState IApplicationState - предзагруженное состояние
 * @param context
 * @param debugEnabled
 */
export function createReduxStore(
  initialState: IApplicationInitialState,
  context: IApplicationContext,
  debugEnabled: boolean,
) {
  const reducers = combineReducers<IApplicationState, TReduxActions>({
    analytics: analyticsReducer,
    breadcrumbs: breadcrumbsReducer,
    error: errorReducer,
    filterSelectors: filterSelectorsReducer,
    hasNexPage: hasNexPageReducer,
    isCompilation: isCompilationReducer,
    jsonQuery: jsonQueryReducer,
    loadingStatus: loadingStatusReducer,
    nextJsonQuery: nextJsonQueryReducer,
    page: noopReducer<TPage>(),
    path: pathReducer,
    promoDetails: promoDetailsReducer,
    promos: promosReducer,
    seoData: seoDataReducer,
    sortVariants: sortVariantsReducer,
    suggestions: suggestionsReducer,
    tags: tagsReducer,
    totalCount: totalCountReducer,
    user: userReducer,
    promoOffersSelection: promoOffersReducer,
    experiments: noopReducer<IExperiment[]>(),
    favoritesAssignWidget: favoritesAssignWidgetReducer,
    listingsUrls: listingsUrlsReducer,
    stickyButton: stickyButtonReducer,
  });

  const composeEnhancers = debugEnabled ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;
  const middlewares = [thunk.withExtraArgument(context)];
  const storeEnchancers = composeEnhancers(applyMiddleware(...middlewares)) as StoreEnhancer<IThunkExt>;

  const createStore = (): TReduxStore =>
    _createStore<CombinedState<IApplicationState>, TReduxActions, IThunkExt, {}>(
      reducers,
      initialState,
      storeEnchancers,
    );

  if (process.env.NODE_ENV === 'development' && debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(reducers);

    return storeReminder;
  }

  return createStore();
}
